import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';

import { FunnelIcon } from '../ui/SortIcons';

/**
 * Single-select column-header filter dropdown.
 *
 * Shared across the Carrier Portal referral table and the Carrier Engagement
 * overview table so the filter affordance stays visually and behaviourally
 * identical everywhere. `options` is a list of `{ value, label }`; selecting
 * one calls `onChange(value)`, and the "All" entry clears the filter via
 * `onChange('')`.
 */
function ColumnFilterDropdown({ options = [], value, onChange, active }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className={`flex items-center justify-center py-1 px-0.5 rounded transition-colors hover:bg-muted-foreground/10 text-muted-foreground ${active ? 'text-primary' : ''}`}
        aria-label="Filter"
        aria-expanded={open}
        data-testid="column-filter-button"
        onClick={() => setOpen((prev) => !prev)}
      >
        <FunnelIcon className={`h-2.5 w-2.5 ${active ? 'opacity-100' : 'opacity-[0.35]'}`} />
      </button>
      {open && (
        <div className="filter-dropdown-menu" data-testid="column-filter-dropdown">
          <button
            type="button"
            className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-filter-bg text-left"
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
          >
            <span className="w-4">{value === '' || value === undefined ? <Check className="h-4 w-4" /> : ''}</span>
            <span>All</span>
          </button>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-filter-bg text-left"
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span className="w-4">{value === option.value ? <Check className="h-4 w-4" /> : ''}</span>
              <span className="filter-dropdown-text" title={option.label}>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { ColumnFilterDropdown };
